import Moment from 'moment';
import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { IStepModuleProps, ModuleSearchParam } from '../../../../models/modules';
import {
    IReservationModel,
    reservationValueChanged,
    IPendingReservation,
} from '../../../../entities/reservation';
import { StepType } from '../../../../services/getStepOrder';
import withSearchParams, { ISearchParamsOptions, getDateParam } from '../withSearchParams';
import { getDate, getTime, setTimeOnDate } from 'src/services/dateTime';
import DateTimeSelect from './DateTimeSelect';
import ValidationMessage from 'src/components/form-components/ValidationMessage/ValidationMessage';
import FormValidator from 'src/services/formValidator';
import { dateTimeSelectRules } from 'src/services/validations/dateTimeSelectRules';
import globalMessages from 'src/services/globalMessages';
import makeCancelable, { ICancelablePromise } from 'src/services/cancelablePromise';
import {
    getCalendarFirstFreeSlot,
    googleSyncByCalendar,
    googleSyncAll,
} from 'src/services/api/calendar';
import AvailabilityWarning from 'src/components/AvailabilityWarning';
import { IResponseDto } from 'src/entities/common';
import { reservationModelToPendingRequest } from 'src/services/api/reservation';
import { getServiceFirstFreeSlot } from 'src/services/api/service';
import FixedButton from 'src/components/form-components/FixedButton/FixedButton';
import AppointmentCalendar from './AppointmentCalendar';
import Placeholder from 'src/components/Placeholder/Placeholder';
import { isAppointmentCalendar, isRandomCalendar, CalendarType } from 'src/entities/calendar';
import { BookingContext } from 'src/contexts/BookingFlowContext';
import { IDateTimeSelectStepState } from 'src/entities/dateTime';

const searchParamsOptions: ISearchParamsOptions<string> = {
    paramName: ModuleSearchParam.Date,
};

const DateTimeSelectStep: React.FC<IStepModuleProps> = ({
    isActive,
    data,
    onLoadingChanged,
    onComplete,
}) => {
    const prevProps = useRef({ isActive, data });
    const intl = useIntl();
    const { reservations } = useContext(BookingContext);
    const firstFreeSlotSubScription = useRef<ICancelablePromise<string>>();
    const googleSyncSubscription = useRef<ICancelablePromise<IResponseDto>>();
    const validator = new FormValidator(dateTimeSelectRules);
    const appointment = data[StepType.Date];
    const [pendingReservations, setPendingReservations] = useState<IPendingReservation[]>([]);
    const [prevHasLoaded, setPrevHasLoaded] = useState(false);
    const [readyToFinish, setReadyToFinish] = useState(false);
    const [state, setState] = useState<IDateTimeSelectStepState>({
        ...(appointment
            ? {
                  personCount: appointment.personCount,
                  selectedDate: getDate(appointment.startTime),
                  selectedTime: getTime(appointment.startTime),
                  submitted: false,
              }
            : {
                  personCount: 1,
                  selectedDate: '',
                  submitted: false,
              }),
        firstFreeSlot: '',
        hasLoaded: false,
        calendarChanged: false,
        clickedByUser: false,
    });
    const {
        selectedDate,
        selectedTime,
        submitted,
        firstFreeSlot,
        hasLoaded,
        calendarChanged,
        clickedByUser,
        personCount,
    } = state;
    const calendar = data[StepType.Calendar];
    const service = data[StepType.Service];
    const validation = submitted
        ? validator.validate({ selectedDate, selectedTime: selectedTime || '' })
        : validator.getInitial();
    const autoSelectedDate = isActive ? getDateParam(data.searchParams) : undefined;
    const isAppointmentView = isAppointmentCalendar(calendar);

    const handleDateTimeChange = useCallback(
        (newDateTime: string) => {
            setState((prevState) => ({ ...prevState, selectedDate: newDateTime, selectedTime: getTime(newDateTime) }));
            setReadyToFinish(true);
        },
        [state]
    );
    const handleDateChange = useCallback(
        (newDate: string) => {
            setState({
                ...state,
                selectedDate: newDate,
                selectedTime: undefined,
                clickedByUser: false,
            });
        },
        [state]
    );
    const handleTimeChange = useCallback(
        (newTime: string, clicked?: boolean) => {
            setState({ ...state, selectedTime: newTime, clickedByUser: clicked ?? false });
        },
        [state]
    );
    const handlePersonCountChange = useCallback(
        (newPersonCount: number) => {
            setState({ ...state, personCount: newPersonCount });
        },
        [state]
    );
    const fetchFirstFreeSlot = () => {
        if (!service) {
            return;
        }
        firstFreeSlotSubScription.current = makeCancelable(
            calendar
                ? getCalendarFirstFreeSlot(calendar.Id, service.Id)
                : getServiceFirstFreeSlot(service.Id)
        );
        firstFreeSlotSubScription.current.promise
            .then((result) => {
                setState((prevState) => ({ ...prevState, firstFreeSlot: result, hasLoaded: true }));
            })
            .catch(() => null);
    };

    const googleSync = () => {
        if (calendar && !isRandomCalendar(calendar.Id) && !calendar.GoogleCalendarName) {
            fetchFirstFreeSlot();
            return;
        }

        if (!calendar || isRandomCalendar(calendar.Id)) {
            googleSyncSubscription.current = makeCancelable(googleSyncAll());
        } else if (calendar.GoogleCalendarName) {
            googleSyncSubscription.current = makeCancelable(googleSyncByCalendar(calendar.Id));
        }

        googleSyncSubscription.current?.promise
            .catch(() => null)
            .finally(() => {
                fetchFirstFreeSlot();
            });
    };

    const finish = () => {
        setState({ ...state, submitted: true });
        if (selectedDate && selectedTime && service) {
            const startTime = Moment.utc(setTimeOnDate(selectedDate, selectedTime)).format();
            const endTime = Moment.utc(startTime).add(service.DurationToUser, 'minutes').format();
            const reservation: IReservationModel = {
                ...data,
                [StepType.Date]: {
                    startTime,
                    endTime,
                    personCount,
                    displayValue: `${intl.formatDate(startTime)}, ${intl.formatTime(startTime)}`,
                },
            };
            onComplete(reservation);
        }
    };

    const recalculatePersonCount = (): number => {
        let newPersonCount = personCount;
        if (calendar && calendar.ConcurrentNum < newPersonCount) {
            newPersonCount = 1;
        }
        return newPersonCount;
    };

    useEffect(() => {
        if (isActive) {
            googleSync();
        }

        setPendingReservations(
            (reservations &&
                (reservations.slice(0, -1) as IReservationModel[])
                    .filter((r) => r.calendar?.Type === CalendarType.Classic)
                    .map((r) => reservationModelToPendingRequest(r))) ||
                []
        );

        return () => {
            firstFreeSlotSubScription.current?.cancel();
            googleSyncSubscription.current?.cancel();
        };
    }, []);

    useEffect(() => {
        if (readyToFinish) {
            finish();
        }
    }, [readyToFinish]);

    useEffect(() => {
        const isCalendarChanged = reservationValueChanged(data, prevProps.current.data, 'calendar');
        if (isCalendarChanged) {
            setState((prevState) => ({
                ...prevState,
                personCount: recalculatePersonCount(),
                calendarChanged: isCalendarChanged,
            }));
        }

        if (!isActive) {
            prevProps.current = { isActive, data };
            return;
        }

        const isServiceChanged = reservationValueChanged(data, prevProps.current.data, 'service');
        const isActiveChanged = prevProps.current.isActive !== isActive;
        if (isActiveChanged || isCalendarChanged || isServiceChanged) {
            setState((prevState) => ({
                ...prevState,
                firstFreeSlot: '',
                submitted: false,
                hasLoaded: false,
            }));
            setReadyToFinish(false);
            googleSync();
        }

        if (prevHasLoaded !== hasLoaded) {
            onLoadingChanged?.(!hasLoaded);
        }

        prevProps.current = { isActive, data };
        setPrevHasLoaded(hasLoaded);
    }, [isActive, data, hasLoaded]);

    if (!hasLoaded) {
        return <Placeholder />;
    }

    if (!firstFreeSlot) {
        return <AvailabilityWarning type="noFreeIntervals" modifier="color-red" />;
    }

    return (
        <>
            {(calendar && service && isAppointmentView && (
                <AppointmentCalendar
                    calendar={calendar}
                    service={service}
                    personCount={personCount}
                    firstFreeSlot={firstFreeSlot}
                    querySelectedDate={autoSelectedDate}
                    onDateTimeSelect={!calendar?.Readonly ? handleDateTimeChange : undefined}
                    onPersonCountChange={handlePersonCountChange}
                    pendingReservations={pendingReservations}
                />
            )) || (
                <DateTimeSelect
                    calendar={calendar}
                    service={service}
                    intl={intl}
                    time={selectedTime}
                    date={selectedDate}
                    firstFreeDay={getDate(firstFreeSlot)}
                    personCount={personCount}
                    autoSelect={autoSelectedDate}
                    onDateChange={handleDateChange}
                    onTimeChange={handleTimeChange}
                    onPersonCountChange={handlePersonCountChange}
                    allowAlreadySelected={false}
                    pendingReservations={pendingReservations}
                    isOnlyService={!calendar}
                    calendarChanged={calendarChanged}
                    clickedByUser={clickedByUser}
                />
            )}
            <div className="validation-message-container">
                <ValidationMessage
                    message={!validation.isValid ? globalMessages.dateTimeRequired : undefined}
                />
            </div>
            {!isAppointmentView && !calendar?.Readonly && isActive && (
                <FixedButton
                    className="date-time-select-step__button"
                    caption={globalMessages.next}
                    onClick={finish}
                />
            )}
        </>
    );
};

export default withSearchParams<IStepModuleProps, object>(DateTimeSelectStep, searchParamsOptions);
